import { MapperSchemaCreateRequest } from "@bluefalcon/clients-management-client-fetch";
import { Box, VStack, Grid, FormControl, Input, IconButton, Flex, Button } from "@chakra-ui/react";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useRef, ChangeEvent } from "react";
import { toUpperSnakeCase } from "../../utils/string.utils";
import { toast } from "../../utils/toast.utils";

type SchemaConnectionProps = {
    onSave: (schma: MapperSchemaCreateRequest[]) => void;
    columns: MapperSchemaCreateRequest[];
};

export const SchemaConnection = ({
    onSave,
    columns,
}: SchemaConnectionProps) => {
    const [pairs, setPairs] = useState(columns);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const text = await file.text();
            const json: Record<string, string> = JSON.parse(text);
            const map: Record<string, string> = {};
            const toBeSet: { key: string, value: string, type: 'columns' }[] = [];

            for (const key in json) {
                if (typeof json[key] === 'string') {
                    map[key] = json[key];
                }
            }

            for (const key in map) {
                toBeSet.push({ key, value: map[key], type: 'columns' });
            }
            setPairs(toBeSet);
        }
    };

    const handleAddPair = () => {
        setPairs([...pairs, { key: '', value: '', type: 'columns' }]);
    };

    const handleRemovePair = (index: number) => {
        const updatedPairs = pairs.filter((_, i) => i !== index);
        setPairs(updatedPairs);
    };

    const handleChange = (index: number, field: string, value: string) => {
        const updatedPairs = pairs.map((pair, i) =>
            i === index ? { ...pair, [field]: value } : pair
        );
        setPairs(updatedPairs);
    };

    const handleSave = () => {
        const valid = pairs
            .filter(({ key, value }) => !!(key.trim() && value.trim()))
            .map(({ key, value, type }) => ({ key: toUpperSnakeCase(key), value: toUpperSnakeCase(value), type }));
        setPairs(valid);
        onSave(valid);
        toast('Saved');
    };

    return (
        <Box
            p={4}
            mt={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="md"
            bg="white"
            w="100%"
            mx="auto"
        >
            <VStack spacing={6} align="stretch">
                {pairs.map((pair, index) => (
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center" key={index}>
                        <FormControl>
                            <Input
                                width={'fit-content'}
                                placeholder="Falcon Key"
                                value={pair.key}
                                onChange={(e) => handleChange(index, 'key', e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <Input
                                placeholder="Carrier Col Name"
                                value={pair.value}
                                onChange={(e) => handleChange(index, 'value', e.target.value)}
                            />
                        </FormControl>

                        <IconButton
                            ml={'auto'}
                            w={'25%'}
                            aria-label="Remove pair"
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => handleRemovePair(index)}
                            colorScheme="red"
                            variant="outline"
                            size="sm"
                        />
                    </Grid>
                ))}

                <Flex justify="flex-end" w="100%">
                    <Button onClick={handleButtonClick} mr={'auto'} size={'sm'} variant={'solid'} colorScheme="warn">JSON</Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".json"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button colorScheme="primary" onClick={handleAddPair} mr={2} size="sm">
                        Add Schema Connection
                    </Button>
                    <Button colorScheme="teal" onClick={handleSave} size="sm">
                        Save
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};